import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { formatConstitution } from '../lib/constitutionLabels';
import { compressImage } from '../components/ProfilePrompt';
import LocationSearch from '../components/LocationSearch';

const ScoreBar = ({ label, score }) => {
  const pct = score ?? 0;
  const color = pct >= 70 ? '#c9a84c' : pct >= 45 ? '#888' : '#444';
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--very-muted)' }}>
          {label}
        </span>
        <span style={{ fontSize: 20, fontWeight: 300, color: score !== null ? '#fff' : 'var(--very-muted)', fontFamily: 'var(--font-display)' }}>
          {score !== null ? score : '—'}
        </span>
      </div>
      <div style={{ height: 2, background: '#222', borderRadius: 1 }}>
        <div style={{ height: 2, width: `${pct}%`, background: color, borderRadius: 1, transition: 'width 1s ease' }} />
      </div>
    </div>
  );
};

export default function PartnerTest() {
  const navigate = useNavigate();
  const fileRef = useRef();

  const [name, setName] = useState('');
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [birthDay, setBirthDay] = useState('');
  const [birthMonth, setBirthMonth] = useState('');
  const [birthYear, setBirthYear] = useState('');
  const [birthHour, setBirthHour] = useState('');
  const [birthMin, setBirthMin] = useState('');
  const [birthLocation, setBirthLocation] = useState(null);

  const [whatWorked, setWhatWorked] = useState('');
  const [challenges, setChallenges] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handlePhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoPreview(URL.createObjectURL(file));
    try {
      const base64 = await compressImage(file);
      setPhoto(base64);
    } catch {
      const reader = new FileReader();
      reader.onload = ev => setPhoto(ev.target.result);
      reader.readAsDataURL(file);
    }
  };

  const canSubmit = photo || (birthDay && birthMonth && birthYear);

  const handleSubmit = async () => {
    if (!canSubmit || loading) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const data = await api.post('/partner-test', {
        name: name.trim() || null,
        photo: photo || null,
        birth_day: birthDay ? parseInt(birthDay) : null,
        birth_month: birthMonth ? parseInt(birthMonth) : null,
        birth_year: birthYear ? parseInt(birthYear) : null,
        birth_hour: birthHour ? parseInt(birthHour) : null,
        birth_min: birthMin ? parseInt(birthMin) : null,
        birth_lat: birthLocation?.lat ?? null,
        birth_lon: birthLocation?.lng ?? null,
        birth_tzone: birthLocation?.tzone ?? null,
        what_worked: whatWorked.trim() || null,
        challenges: challenges.trim() || null,
      });
      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0d0d0d', color: '#fff', padding: '0 0 80px 0' }}>

      {/* Header */}
      <div style={{ padding: '56px 24px 0' }}>
        <button
          onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: 13, cursor: 'pointer', padding: 0, marginBottom: 32 }}
        >
          ← Back
        </button>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 300, marginBottom: 8 }}>
          Test My Partner
        </div>
        <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6, marginBottom: 40 }}>
          Upload their photo and birth date. No account needed on their end.
        </div>
      </div>

      <div style={{ padding: '0 24px' }}>

        {/* Name */}
        <div style={{ marginBottom: 32 }}>
          <div style={{ fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--very-muted)', marginBottom: 12 }}>
            Their name <span style={{ color: '#444', textTransform: 'none', letterSpacing: 0 }}>— optional</span>
          </div>
          <input
            type="text"
            placeholder="e.g. Alex"
            value={name}
            onChange={e => setName(e.target.value)}
            style={inputStyle}
          />
        </div>

        {/* Photo upload */}
        <div style={{ marginBottom: 32 }}>
          <div style={{ fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--very-muted)', marginBottom: 8 }}>
            Their photo
          </div>
          <div style={{ fontSize: 12, color: 'rgba(201,168,76,0.65)', lineHeight: 1.55, marginBottom: 12 }}>
            Please upload a clear, front-facing headshot — we run a face scan to determine their energy type.
          </div>
          <div
            onClick={() => fileRef.current?.click()}
            style={{
              width: '100%', height: 180,
              border: '1px solid #2a2a2a',
              borderRadius: 8,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer',
              overflow: 'hidden',
              position: 'relative',
              background: '#111',
            }}
          >
            {photoPreview ? (
              <img src={photoPreview} alt="partner" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <div style={{ textAlign: 'center', color: 'var(--very-muted)' }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>+</div>
                <div style={{ fontSize: 12 }}>Tap to upload photo</div>
              </div>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/*" onChange={handlePhoto} style={{ display: 'none' }} />
        </div>

        {/* Birth date */}
        <div style={{ marginBottom: 32 }}>
          <div style={{ fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--very-muted)', marginBottom: 12 }}>
            Their birth date
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.5fr', gap: 10 }}>
            <input
              type="number" placeholder="Day" min="1" max="31"
              value={birthDay} onChange={e => setBirthDay(e.target.value)}
              style={inputStyle}
            />
            <input
              type="number" placeholder="Month" min="1" max="12"
              value={birthMonth} onChange={e => setBirthMonth(e.target.value)}
              style={inputStyle}
            />
            <input
              type="number" placeholder="Year"
              value={birthYear} onChange={e => setBirthYear(e.target.value)}
              style={inputStyle}
            />
          </div>
        </div>

        {/* Birth city */}
        <div style={{ marginBottom: 32 }}>
          <div style={{ fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--very-muted)', marginBottom: 12 }}>
            Birth city
          </div>
          <LocationSearch
            placeholder="City or town..."
            onSelect={(coords) => setBirthLocation(coords)}
          />
        </div>

        {/* Birth time (optional) */}
        <div style={{ marginBottom: 40 }}>
          <div style={{ fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--very-muted)', marginBottom: 4 }}>
            Birth time <span style={{ color: '#444', textTransform: 'none', letterSpacing: 0 }}>— optional, improves accuracy</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 12 }}>
            <input
              type="number" placeholder="Hour (0–23)"
              value={birthHour} onChange={e => setBirthHour(e.target.value)}
              style={inputStyle}
            />
            <input
              type="number" placeholder="Min (0–59)"
              value={birthMin} onChange={e => setBirthMin(e.target.value)}
              style={inputStyle}
            />
          </div>
        </div>

        {/* Relationship context */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--very-muted)', marginBottom: 4 }}>
            What worked well <span style={{ color: '#444', textTransform: 'none', letterSpacing: 0 }}>— optional</span>
          </div>
          <div style={{ fontSize: 12, color: '#444', marginBottom: 10, lineHeight: 1.5 }}>
            What felt good, aligned, or easy in this relationship
          </div>
          <textarea
            rows={3}
            value={whatWorked}
            onChange={e => setWhatWorked(e.target.value)}
            placeholder="e.g. Great communication, same life values, felt safe..."
            style={{ ...inputStyle, resize: 'none', lineHeight: 1.55, padding: '12px', fontFamily: 'inherit' }}
          />
        </div>

        <div style={{ marginBottom: 40 }}>
          <div style={{ fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--very-muted)', marginBottom: 4 }}>
            Challenges <span style={{ color: '#444', textTransform: 'none', letterSpacing: 0 }}>— optional</span>
          </div>
          <div style={{ fontSize: 12, color: '#444', marginBottom: 10, lineHeight: 1.5 }}>
            What was hard, misaligned, or caused recurring friction
          </div>
          <textarea
            rows={3}
            value={challenges}
            onChange={e => setChallenges(e.target.value)}
            placeholder="e.g. Different priorities around time, emotional unavailability..."
            style={{ ...inputStyle, resize: 'none', lineHeight: 1.55, padding: '12px', fontFamily: 'inherit' }}
          />
        </div>

        {/* Submit */}
        {!result && (
          <button
            onClick={handleSubmit}
            disabled={!canSubmit || loading}
            style={{
              width: '100%', padding: '16px 0', borderRadius: 8, border: 'none',
              background: canSubmit && !loading ? '#c9a84c' : '#1e1e1e',
              color: canSubmit && !loading ? '#000' : '#444',
              fontSize: 14, letterSpacing: 1, cursor: canSubmit && !loading ? 'pointer' : 'default',
              transition: 'all 0.2s',
            }}
          >
            {loading ? 'Analysing...' : 'Run Compatibility'}
          </button>
        )}

        {error && (
          <div style={{ marginTop: 16, fontSize: 13, color: '#c0392b', textAlign: 'center' }}>{error}</div>
        )}

        {/* Results */}
        {result && (
          <div style={{ marginTop: 8 }}>
            <div style={{ height: 1, background: '#1e1e1e', marginBottom: 36 }} />

            {result.partner_dosha && (
              <div style={{ marginBottom: 32, textAlign: 'center' }}>
                <div style={{ fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--very-muted)', marginBottom: 6 }}>
                  Their energy pattern
                </div>
                <div style={{ fontSize: 22, fontFamily: 'var(--font-display)', fontWeight: 300 }}>
                  {formatConstitution(result.partner_dosha)}
                </div>
              </div>
            )}

            <ScoreBar label="Attraction" score={result.attraction_score} />
            <ScoreBar label="Bio-Harmonics" score={result.bio_harmonics} />
            <ScoreBar label="Chrono-Dynamics" score={result.chrono_dynamics} />

            {result.summary && (
              <>
                <div style={{ height: 1, background: '#1e1e1e', margin: '32px 0' }} />
                <div style={{ fontSize: 15, lineHeight: 1.75, color: 'var(--muted)', fontWeight: 300 }}>
                  {result.summary}
                </div>
              </>
            )}

            <div style={{ marginTop: 32, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 11, color: 'var(--very-muted)' }}>Saved to your partner repository</span>
              <button
                onClick={() => navigate('/settings/partners')}
                style={{ background: 'none', border: 'none', color: 'var(--gold)', fontSize: 11, cursor: 'pointer', padding: 0 }}
              >
                View all →
              </button>
            </div>

            <button
              onClick={() => { setResult(null); setName(''); setPhoto(null); setPhotoPreview(null); setBirthDay(''); setBirthMonth(''); setBirthYear(''); setBirthHour(''); setBirthMin(''); setBirthLocation(null); setWhatWorked(''); setChallenges(''); }}
              style={{ marginTop: 16, width: '100%', padding: '14px 0', borderRadius: 8, border: '1px solid #2a2a2a', background: 'none', color: 'var(--muted)', fontSize: 13, cursor: 'pointer' }}
            >
              Test another
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const inputStyle = {
  width: '100%',
  padding: '14px 12px',
  background: '#111',
  border: '1px solid #2a2a2a',
  borderRadius: 8,
  color: '#fff',
  fontSize: 15,
  outline: 'none',
  boxSizing: 'border-box',
};
